import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Factory, MapPin, Phone, User, Building2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';

const PlantasListPage = () => {
  const [plantas, setPlantas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  const loadPlantas = useCallback(async () => {
    setIsLoading(true);
    try {
      const { plantasService } = await import('@/pages/parametrizacion/plantas/utils/plantasService');
      const response = await plantasService.getAll();
      const plantasData = response?.data || [];
      // Filtrar solo plantas activas
      setPlantas(plantasData.filter(planta => planta.estado === 'Activo'));
    } catch (error) {
      console.error('Error cargando plantas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las plantas.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadPlantas();
  }, [loadPlantas]);

  const handlePlantaClick = (planta) => {
    // Navegar al dashboard de la planta
    navigate(`/plantas/${planta._id || planta.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto p-4 md:p-6 lg:p-8"
    >
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-2">
          <div className="bg-primary/10 p-3 rounded-lg">
            <Factory className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Plantas</h1>
            <p className="text-muted-foreground">Selecciona una planta para ver su información</p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-32 bg-muted rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : plantas.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Factory className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No hay plantas disponibles</h3>
            <p className="text-muted-foreground">
              No se encontraron plantas activas en el sistema.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plantas.map((planta) => (
            <motion.div
              key={planta._id || planta.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <Card 
                className="cursor-pointer hover:shadow-lg transition-all duration-200 border-2 hover:border-primary"
                onClick={() => handlePlantaClick(planta)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 p-2 rounded-lg">
                        <Factory className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-xl">{planta.nombre}</CardTitle>
                        <CardDescription className="flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3" />
                          {planta.ciudad}
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-muted-foreground text-xs">Dirección</p>
                      <p className="text-foreground">{planta.direccion}</p>
                    </div>
                  </div>
                  
                  {planta.telefono && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div>
                        <p className="text-muted-foreground text-xs">Teléfono</p>
                        <p className="text-foreground">{planta.telefono}</p>
                      </div>
                    </div>
                  )}
                  
                  {planta.encargado && (
                    <div className="flex items-center gap-2 text-sm">
                      <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div>
                        <p className="text-muted-foreground text-xs">Encargado</p>
                        <p className="text-foreground">{planta.encargado}</p>
                      </div>
                    </div>
                  )}

                  <div className="pt-3 border-t">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Estado</span>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#7ac143] text-white">
                        {planta.estado}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default PlantasListPage;
